import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, ArrowUpDown, Trash2, ArrowRight } from "lucide-react";
import { useContentStore, EMPTY_KEYWORDS, type KeywordResearch } from "@/stores/contentStore";
import { useToast } from "@/hooks/use-toast";
import { NewArticleModal } from "./NewArticleModal";

interface KeywordExplorerProps {
  projectId: number;
}

type SortField = "keyword" | "search_volume" | "keyword_difficulty" | "priority";
type FilterMode = "all" | "unused" | "used";

export function KeywordExplorer({ projectId }: KeywordExplorerProps) {
  const keywords = useContentStore((s) => s.keywords[projectId] ?? EMPTY_KEYWORDS);
  const fetchKeywords = useContentStore((s) => s.fetchKeywords);
  const deleteKeyword = useContentStore((s) => s.deleteKeyword);
  const runResearch = useContentStore((s) => s.runKeywordResearch);
  const queueKeyword_ = useContentStore((s) => s.queueKeywordAsContent);
  const loading = useContentStore((s) => s.loading[`keywords-${projectId}`]);
  const researching = useContentStore((s) => s.loading[`research-${projectId}`]);
  const { toast } = useToast();

  const [sortField, setSortField] = useState<SortField>("priority");
  const [sortAsc, setSortAsc] = useState(true);
  const [filter, setFilter] = useState<FilterMode>("all");
  const [queueKeyword, setQueueKeyword] = useState<string | null>(null);

  useEffect(() => {
    void fetchKeywords(projectId);
  }, [projectId, fetchKeywords]);

  const filtered = useMemo(() => {
    let result = [...keywords];
    if (filter === "unused") result = result.filter((k) => !k.content_item_id);
    if (filter === "used") result = result.filter((k) => !!k.content_item_id);
    return result;
  }, [keywords, filter]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case "keyword":
          cmp = a.keyword.localeCompare(b.keyword);
          break;
        case "search_volume":
          cmp = (a.search_volume || 0) - (b.search_volume || 0);
          break;
        case "keyword_difficulty":
          cmp = (a.keyword_difficulty || 0) - (b.keyword_difficulty || 0);
          break;
        case "priority": {
          const order: Record<string, number> = { high: 0, medium: 1, low: 2 };
          cmp = (order[a.priority] ?? 3) - (order[b.priority] ?? 3);
          break;
        }
      }
      return sortAsc ? cmp : -cmp;
    });
  }, [filtered, sortField, sortAsc]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const handleDelete = async (kw: KeywordResearch) => {
    try {
      await deleteKeyword(kw.id, projectId);
    } catch {
      toast({ title: "Delete failed", variant: "destructive" });
    }
  };

  const geoScore = (kw: KeywordResearch) =>
    (kw.metadata as Record<string, unknown>)?.geo_score as number | undefined;

  const priorityColor: Record<string, string> = {
    high: "text-red-400 border-red-400/30",
    medium: "text-amber-400 border-amber-400/30",
    low: "text-green-400 border-green-400/30",
  };

  const SortButton = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <button
      onClick={() => toggleSort(field)}
      aria-label={`Sort by ${field.replace("_", " ")}${sortField === field ? (sortAsc ? ", ascending" : ", descending") : ""}`}
      className="flex items-center gap-1 hover:text-foreground transition-colors"
    >
      {children}
      <ArrowUpDown className="w-3 h-3" aria-hidden="true" />
    </button>
  );

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={(v) => setFilter(v as FilterMode)}>
            <SelectTrigger className="w-[120px] h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="unused">Unused</SelectItem>
              <SelectItem value="used">Used</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">
            {sorted.length} keyword{sorted.length !== 1 && "s"}
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={researching}
          onClick={async () => {
            try {
              await runResearch(projectId);
              toast({ title: "Keyword research started" });
            } catch {
              toast({ title: "Research failed", variant: "destructive" });
            }
          }}
        >
          {researching ? "Researching..." : "Run Research"}
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-md border border-border/40">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead><SortButton field="keyword">Keyword</SortButton></TableHead>
              <TableHead className="w-[100px]"><SortButton field="search_volume">Volume</SortButton></TableHead>
              <TableHead className="w-[100px]"><SortButton field="keyword_difficulty">Difficulty</SortButton></TableHead>
              <TableHead className="w-[80px]">GEO</TableHead>
              <TableHead className="w-[100px]">Intent</TableHead>
              <TableHead className="w-[80px]"><SortButton field="priority">Priority</SortButton></TableHead>
              <TableHead className="w-[80px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  No keywords found
                </TableCell>
              </TableRow>
            ) : (
              sorted.map((kw) => (
                <TableRow key={kw.id}>
                  <TableCell className="font-medium">{kw.keyword}</TableCell>
                  <TableCell>{kw.search_volume?.toLocaleString() ?? "—"}</TableCell>
                  <TableCell>
                    {kw.keyword_difficulty !== null ? (
                      <div className="flex items-center gap-2">
                        <div className="w-12 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full bg-blue-500"
                            style={{ width: `${kw.keyword_difficulty}%` }}
                          />
                        </div>
                        <span className="text-xs">{kw.keyword_difficulty}</span>
                      </div>
                    ) : "—"}
                  </TableCell>
                  <TableCell>{geoScore(kw) ?? "—"}</TableCell>
                  <TableCell>
                    {kw.intent ? (
                      <Badge variant="outline" className="text-xs">{kw.intent}</Badge>
                    ) : "—"}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-xs ${priorityColor[kw.priority] || ""}`}>
                      {kw.priority}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {!kw.content_item_id && (
                        <button
                          onClick={async () => {
                            try {
                              await queueKeyword_(kw.id, projectId);
                              toast({ title: `Queued: ${kw.keyword}` });
                            } catch {
                              toast({ title: "Queue failed", variant: "destructive" });
                            }
                          }}
                          className="p-1 text-muted-foreground hover:text-blue-400 transition-colors"
                          aria-label={`Quick queue ${kw.keyword}`}
                          title="Quick queue as content item"
                        >
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => setQueueKeyword(kw.keyword)}
                        className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                        aria-label={`Create article from ${kw.keyword}`}
                        title="Create article from keyword"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(kw)}
                        className="p-1 text-muted-foreground hover:text-red-400 transition-colors"
                        aria-label={`Delete ${kw.keyword}`}
                        title="Delete keyword"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <NewArticleModal
        isOpen={!!queueKeyword}
        onClose={() => setQueueKeyword(null)}
        projectId={projectId}
        prefillKeyword={queueKeyword || undefined}
      />
    </div>
  );
}
